import { useMediaQuery } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { sendTrackingEvent } from "../../../../helpers/tracking_management";
import MobileAppAd from "../MobileAppAd/MobileAppAd";
import SimilarJobCard from "../SimilarJobCard/SimilarJobCard";
import SimilarJobSkeleton from "../SimilarJobCard/SimilarJobSkeleton";
import {
  BoxContainer,
  CheckboxHover,
  CheckboxIcon,
  CheckboxStyled,
  CheckboxWrapper,
  CheckedBox,
  HeaderStyled,
  UncheckedBox,
} from "../styles";
import {
  BulkJobWrapper,
  CardListContainer,
  HeaderContainer,
  ScrollIndicator,
  ScrollIndicatorContainer,
  SelectAllButton,
} from "./styles";

export default function BulkApplyList(props) {
  const {
    dialog,
    showList,
    secondaryBulk,
    selectedJobs,
    setSelectedJobs,
    fetchingJobs,
    jobs,
    jobAlertPage,
    setViewedCards,
    jobListRef,
    currentTab,
    currentPage,
    recommendedJobs,
    origin,
    postApplyImpressionOrigin,
    specialisationTitle,
  } = props;

  const isMobile = useMediaQuery("(max-width: 640px)");
  const { t } = useTranslation();

  const smallCard = showList && secondaryBulk;

  const [hover, setHover] = useState(false);
  const [inView, setInView] = useState(null);
  const similarJobsTriggered = useSelector(
    (state) => state.dialog?.showSimilarJobs
  );

  function checkedJob(job) {
    // Check if the job is in the selectedJobs array
    return selectedJobs?.map((job) => job.id).includes(job?.id);
  }

  const checkedJobs = selectedJobs?.length > 0;
  const headerText = jobAlertPage ? (
    t("jobs.bulk_apply_list.review_jobs_below")
  ) : specialisationTitle ? (
    isMobile ? (
      t("jobs.bulk_apply_list.internship_jobs")
    ) : (
      <>
        {t("jobs.bulk_apply_list.internship_jobs_under")}{" "}
        <u>{specialisationTitle}</u>
      </>
    )
  ) : (
    t("jobs.bulk_apply_list.similar_jobs_for_you")
  );

  const handleSelectText = useMemo(() => {
    if (checkedJobs) return t("jobs.bulk_apply_list.deselect_all");
    return t("jobs.bulk_apply_list.select_all");
  }, [checkedJobs, t]);

  function handleSelectAllJobs() {
    setSelectedJobs(jobs?.map((job) => job));
  }

  function handleDeselectAllJobs() {
    setSelectedJobs([]);
  }

  function handleSelectFunction() {
    const checkedJobsIds = selectedJobs?.map((jobs) => jobs?.id);
    const jobIds = jobs?.map((job) => job?.id) || [];

    if (checkedJobs) {
      if (currentTab === "internship-bulk") {
        sendTrackingEvent({
          event: "CE_bulk_job_deselect_all",
          origin: "internship_bank_page",
          selection_type: "deselect all",
          is_selected: "false",
          job_ids: checkedJobsIds,
          timestamp: new Date().toISOString(),
        });
      }

      handleDeselectAllJobs();
    } else {
      if (currentTab === "internship-bulk") {
        sendTrackingEvent({
          event: "CE_bulk_job_select_all",
          origin: "internship_bank_page",
          selection_type: "select all",
          is_selected: "true",
          job_ids: jobIds,
          timestamp: new Date().toISOString(),
        });
      }
      handleSelectAllJobs();
    }
  }

  function handleSelectJob(job) {
    const isSelected = selectedJobs.some((j) => j.id === job.id);
    // 🔹 Tracking — fires ONCE per toggle
    handleSelectJobTracking(job, !isSelected);

    if (isSelected) {
      setSelectedJobs((prev) => prev.filter((j) => j.id !== job.id));
    } else {
      setSelectedJobs((prev) => [...prev, job]);
    }
  }

  function handleSelectJobTracking(job) {
    const trackingPayload = {
      origin: "internship_bank_page",
      job_ids: [job?.id],
      timestamp: new Date().toISOString(),
    };

    if (selectedJobs.map((job) => job.id).includes(job?.id)) {
      if (currentTab === "internship-bulk") {
        sendTrackingEvent({
          event: "CE_bulk_job_deselect_individual",
          selection_type: "deselect individual",
          is_selected: "false",
          ...trackingPayload,
        });
      }
    } else {
      if (currentTab === "internship-bulk") {
        sendTrackingEvent({
          event: "CE_bulk_job_select_individual",
          selection_type: "select individual",
          is_selected: "true",
          ...trackingPayload,
        });
      }
    }
  }

  useEffect(() => {
    handleSelectAllJobs();
  }, [jobs, recommendedJobs]);

  return (
    <BulkJobWrapper $dialog={dialog}>
      {jobs?.length > 0 || fetchingJobs ? (
        <HeaderContainer
          $similarJobsTriggered={similarJobsTriggered}
          $dialog={dialog}
        >
          <HeaderStyled $similarJobsTriggered={similarJobsTriggered}>
            {headerText}
          </HeaderStyled>
          <SelectAllButton onClick={handleSelectFunction}>
            {handleSelectText}
            <CheckboxWrapper
              onMouseEnter={() => setHover(true)}
              onMouseLeave={() => setHover(false)}
            >
              <CheckboxStyled
                icon={<UncheckedBox size={"small"} />}
                checked={checkedJobs}
                checkedIcon={
                  <CheckedBox size={"small"}>
                    <CheckboxIcon size={"small"} />
                  </CheckedBox>
                }
              />
              <CheckboxHover>
                {/* Remove the hover icon if checkbox is checked */}
                {!checkedJobs && <CheckboxIcon hover={hover} size={"small"} />}
              </CheckboxHover>
            </CheckboxWrapper>
          </SelectAllButton>
        </HeaderContainer>
      ) : null}
      <BoxContainer flexDirection={"column"} height={"100%"}>
        <CardListContainer
          ref={jobListRef}
          $dialog={dialog}
          $secondaryBulk={secondaryBulk}
          $showList={showList}
          $jobAlertPage={jobAlertPage}
          $noResult={jobs?.length === 0}
          $similarJobsTriggered={similarJobsTriggered}
        >
          {fetchingJobs ? (
            Array.from({ length: 6 }).map((_, index) => {
              return <SimilarJobSkeleton key={index} dialog={dialog} />;
            })
          ) : jobs?.length > 0 ? (
            jobs.map((job, index) => {
              return (
                <SimilarJobCard
                  key={index}
                  index={index}
                  job={job}
                  smallCard={smallCard}
                  setInView={setInView}
                  setViewedCards={setViewedCards}
                  checkedJob={checkedJob}
                  handleSelectJob={handleSelectJob}
                  dialog={dialog}
                  currentTab={currentTab}
                  origin={origin}
                  similarJobsTriggered={similarJobsTriggered}
                  postApplyImpressionOrigin={postApplyImpressionOrigin}
                />
              );
            })
          ) : (
            <MobileAppAd />
          )}
        </CardListContainer>

        {isMobile && (
          <ScrollIndicatorContainer>
            {Array.from({ length: jobs?.length }).map((_, index) => {
              return (
                <ScrollIndicator key={index} selected={inView === index} />
              );
            })}
          </ScrollIndicatorContainer>
        )}
      </BoxContainer>
    </BulkJobWrapper>
  );
}
